import {
  addToast,
  Button,
  ErrorToast,
  SuccessToast,
} from '@otter-ai/den-components';
import { ZoomIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { FormattedText } from '@/shared/components/FormattedText';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { BUILT_IN_APPS } from '@/shared/types/apps';
import { descriptionFormatRules } from '../../../shared/utils/text';
import { AppRowIds } from '../../constants/row-ids';
import { useSyncedAccounts } from '../../hooks/useSyncedAccounts';
import { useReconnectZoomAccount } from '../../hooks/zoom/useReconnectZoomAccount';
import { useZoomOAuth } from '../../hooks/zoom/useZoomOAuth';
import { ZOOM_TEST_IDS } from '../../test-ids/zoom';
import { trackZoom } from '../../tracking/zoom-tracking';
import type { ZoomSyncedAccount } from '../../type/apps';
import { showZoomReconnect } from '../../utils/zoom';
import { AppRowLayout } from '../AppRowLayout';
import { DisconnectedButton } from '../DisconnectedButton';
import { MoreZoomIntegrations } from './MoreZoomIntegrations';
import { UpsellForMoreZoomIntegrations } from './UpsellForMoreZoomIntegrations';
import { ZoomSettingsButton } from './ZoomSettingsButton';

/**
 * Main component for managing Zoom integration in the account settings.
 * Handles connecting and disconnecting Zoom accounts, and displays additional
 * integration settings when accounts are connected.
 */
export function ZoomAppRow() {
  const t = useTranslations('settings:integrations.apps.zoom');
  const {
    accounts,
    isLoading,
    hasSyncedAccounts: isConnected,
  } = useSyncedAccounts({
    service: BUILT_IN_APPS.ZOOM,
  });
  const {
    prefetchData,
    openZoomAuthorizationPage,
    disconnectZoomFromOtterAccount,
  } = useZoomOAuth();
  const { reconnectZoomAccount } = useReconnectZoomAccount();
  prefetchData();

  const handleDisconnect = useCallback(
    async (account: ZoomSyncedAccount) => {
      trackZoom.main.disconnectZoom();
      if (accounts.length === 0) {
        addToast(<ErrorToast description={t('noZoomAccountsConnected')} />);
        return false;
      }

      const confirmed = await openConfirmDialog({
        title: t('disconnectDialogTitle'),
        message: t('disconnectDialogMessage'),
        confirmText: t('disconnectConfirmButton'),
        danger: true,
      });
      if (!confirmed) {
        return true;
      }

      try {
        await disconnectZoomFromOtterAccount({ email: account.account });
        addToast(
          <SuccessToast description={t('successfullyDisconnectedZoom')} />,
        );
        trackZoom.main.disconnectZoomSuccess();
        return true;
      } catch {
        addToast(<ErrorToast description={t('failedToDisconnectZoom')} />);
        return false;
      }
    },
    [disconnectZoomFromOtterAccount, accounts],
  );

  const handleReconnect = useCallback(
    async (account: ZoomSyncedAccount) => {
      trackZoom.main.reconnectZoom();
      try {
        await reconnectZoomAccount([account]);
        // TODO(sleahy): Add success toast
        return true;
      } catch {
        // TODO(sleahy): Add error toast
        return false;
      }
    },
    [reconnectZoomAccount],
  );

  const showReconnectRequired = useMemo(() => {
    return showZoomReconnect(accounts as ZoomSyncedAccount[]);
  }, [accounts]);

  return (
    <AppRowLayout
      appRowId={AppRowIds.ZOOM}
      Icon={ZoomIcon}
      title="Zoom"
      description={
        <Description showReconnectRequired={showReconnectRequired} />
      }
      extendContent={
        <>
          <UpsellForMoreZoomIntegrations />
          <MoreZoomIntegrations />
        </>
      }
      buttons={
        <div className="flex flex-col gap-2">
          {/* Multiple Zoom accounts are not supported */}
          {!isConnected && (
            <Button
              disabled={isLoading}
              onClick={() => {
                trackZoom.main.connectZoom();
                openZoomAuthorizationPage();
              }}
              data-testid={ZOOM_TEST_IDS.connectButton}
            >
              {t('connectButton')}
            </Button>
          )}
        </div>
      }
      linkedAccounts={accounts.map((account) => ({
        accountName: account.account,
        accountId: account.zoom_credential_id,
        button: (
          <div className="flex flex-row gap-2">
            <ZoomSettingsButton />
            {showZoomReconnect([account]) ? (
              <DisconnectedButton
                disconnectButtonText={t('reconnectButton')}
                disconnectAction={() => handleReconnect(account)}
                tooltipCaption={t('reconnectTooltip', {
                  accountName: account.account,
                })}
                data-testid={ZOOM_TEST_IDS.disconnectButton}
              />
            ) : (
              <DisconnectedButton
                disconnectButtonText={t('disconnectButton')}
                disconnectAction={() => handleDisconnect(account)}
                tooltipCaption={t('disconnectTooltip', {
                  accountName: account.account,
                })}
                data-testid={ZOOM_TEST_IDS.disconnectButton}
              />
            )}
          </div>
        ),
      }))}
    />
  );
}

function Description({
  showReconnectRequired,
}: {
  showReconnectRequired: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.zoom');
  return (
    <FormattedText>
      {t.rich(
        'description',
        descriptionFormatRules({
          links: { 'help-link': { href: t('helpUrl'), target: '_blank' } },
        }),
      )}
      {showReconnectRequired && <p>{t('reconnectRequired')}</p>}
    </FormattedText>
  );
}
